import { gridClasses, GridColDef, GridEventListener, GridRenderCellParams, GridRowId, useGridApiRef } from "@mui/x-data-grid-premium"
import { useCallback, useState } from "react"
import { GroupAction } from "./Groups"
import { GroupRowMenu } from "./GroupMenu"

import moment from "moment"
import { useTimezone } from "lib/TimezoneProvider"
import DataGrid from "components/DataGrid/DataGrid"


export type GroupRow = {
  id: string
  lastUpdated: Date
  createdByName: string
  name: string
  description: string
  membersCount: number
  usedInCount: number
  recipients: string[]
}

type GroupTableProps = {
  data: GroupRow[]
  dispatch: React.Dispatch<GroupAction>
}

export default function GroupTable({ data, dispatch }: GroupTableProps) {
  const apiRef = useGridApiRef()
  const { timezone } = useTimezone()
  const [menuRowId, setMenuRowId] = useState<GridRowId | null>(null)

  const rowUsedInCount = data.find(row => row.id === menuRowId)?.usedInCount
  const disableDelete = rowUsedInCount === undefined ? true : rowUsedInCount > 0

  const columns: GridColDef[] = [
    {
      headerName: "Last Updated",
      field: "lastUpdated",
      type: 'dateTime',
      valueGetter: (value) => new Date(value),
      valueFormatter: (value) =>
        moment(value as Date).tz(timezone).format('M/D/YY, h:mm A'),
      flex: 2,
      minWidth: 156,
      disableColumnMenu: true,
    },
    {
      headerName: "Created By",
      field: "createdByName",
      type: "string",
      flex: 1.5,
      minWidth: 150,
      disableColumnMenu: true,
    },
    {
      headerName: "Name",
      field: "name",
      type: "string",
      flex: 1.5,
      minWidth: 150,
      disableColumnMenu: true,
    },
    {
      headerName: "Description",
      field: "description",
      type: "string",
      flex: 3,
      minWidth: 250,
      disableColumnMenu: true,
    },
    {
      headerName: "Members",
      field: "membersCount",
      type: "number",
      flex: 1,
      minWidth: 80,
      disableColumnMenu: true,
    },
    {
      headerName: "Used In",
      field: "usedInCount",
      type: "number",
      flex: 1,
      minWidth: 80,
      disableColumnMenu: true,
    },
    {
      field: 'id',
      headerName: '',
      type: 'string',
      renderCell: (params: GridRenderCellParams) => (
        <GroupRowMenu rowNode={params.rowNode} dispatch={dispatch} setMenuRowId={setMenuRowId} disableDelete={disableDelete} />
      ),
      sortable: false,
      filterable: false,
      align: 'right',
      width: 52,
      disableColumnMenu: true,
      pinnable: false,
      headerClassName: 'menu-col',
      cellClassName: 'menu-col',
    }
  ]

  const onRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      dispatch({ type: 'OPEN_VIEW', groupId: row.id })
    },
    [dispatch],
  )

  return (
    <DataGrid
      columns={columns}
      rows={data || []}
      apiRef={apiRef}
      onRowClick={onRowClick}
      getRowClassName={(params) => (params.id === menuRowId ? 'menuOpen' : '')}
      initialState={{
        pinnedColumns: { right: ['id'] },
      }}
      sx={{
        '.MuiDataGrid-footerContainer': {display: 'none'},
        "& .actionButton": {
          opacity: 0,
        },
        [`& .${gridClasses.row}:hover .actionButton, & .${gridClasses.row}.menuOpen .actionButton`]: {
          opacity: 1,
        },
        '& .MuiDataGrid-columnHeader.menu-col, & .MuiDataGrid-cell.menu-col': {
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      }}
    />
  )
}
